// src/modules/app-views/omnichannel/pages/Init/modals/OmnichannelParametersModal/modals/EditMetaPhoneModal/index.jsx
// *******************************************************************************
// EDIT META PHONE MODAL
// Single-step modal for editing a Meta WhatsApp phone.
// Editable: avatar, displayName, accessToken (optional).
// Read-only: phoneNumber, origin.
// Token is validated server-side before saving — 422 means invalid token.
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useMemo, useRef, useState } from 'react';
import { Descriptions, Dropdown, Flex, Upload } from 'antd';
import { CameraOutlined, DeleteOutlined, UploadOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { parsePhoneToDisplay } from 'utils/helpers';
import translateX from 'utils/translateX';
import GenericModal from 'components/core/GenericModal';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimplePassword from 'components/SimpleComponents/SimplePassword';
import AvatarCropModal from 'layouts/SharedComponents/AvatarCropModal';
import WebcamCaptureModal from 'layouts/SharedComponents/WebcamCaptureModal';
import appService from 'services/appService';
import omnichannelService from 'modules/app-views/omnichannel/service';
import WhatsAppSourceTag from '../../../../components/WhatsAppSourceTag';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MIN_RESOLUTION = 192;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const EditMetaPhoneContent = ({ editingRecord, avatarDataRef }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const uploadRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const formattedPhone = formatPhone(editingRecord?.phoneNumber);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [profilePicture, setProfilePicture] = useState(editingRecord?.profilePictureBase64 || null);
  const [uploading, setUploading] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [webcamModalOpen, setWebcamModalOpen] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imageFromWebcam, setImageFromWebcam] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const validateFile = useCallback(file => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      appService.message({ flag: 'e', text: translateX('invalid_image_type') });
      return false;
    }
    if (file.size > MAX_FILE_BYTES) {
      appService.message({ flag: 'e', text: translateX('file_too_large_max_2mb') });
      return false;
    }
    return true;
  }, []);

  const beforeUpload = useCallback(async file => {
    if (!validateFile(file)) return Upload.LIST_IGNORE;
    const isValid = await validateFileResolution(file);
    if (!isValid) return Upload.LIST_IGNORE;
    setSelectedImageFile(file);
    setImageFromWebcam(false);
    setCropModalOpen(true);
    return false;
  }, [validateFile]);

  // CropModal: first call is preview + save HQ file, second call is Firebase done
  // Avatar is NOT saved to backend here — deferred to onFinish
  const handleCropConfirm = useCallback(avatarData => {
    if (!avatarData) return;

    if (avatarData.isUploading) {
      setProfilePicture(avatarData.previewUrl);
      setCropModalOpen(false);
      setUploading(true);
      // Save original HQ file so onFinish can read full-resolution base64
      if (selectedImageFile) {
        avatarDataRef.current = { file: selectedImageFile, hasChanged: true };
      }
    } else {
      setUploading(false);
      if (avatarData.base64Thumb) {
        setProfilePicture(avatarData.base64Thumb);
      }
      setSelectedImageFile(null);
    }
  }, [selectedImageFile, avatarDataRef]);

  const handleCropCancel = useCallback(() => {
    setCropModalOpen(false);
    setSelectedImageFile(null);
    setImageFromWebcam(false);
  }, []);

  const handleWebcamCapture = useCallback(async file => {
    setWebcamModalOpen(false);
    if (file && validateFile(file)) {
      const isValid = await validateFileResolution(file);
      if (!isValid) return;
      setSelectedImageFile(file);
      setImageFromWebcam(true);
      setCropModalOpen(true);
    }
  }, [validateFile]);

  const handleRetakePhoto = useCallback(() => {
    setCropModalOpen(false);
    setSelectedImageFile(null);
    setWebcamModalOpen(true);
  }, []);

  const handleRemove = useCallback(() => {
    setProfilePicture(null);
    avatarDataRef.current = { file: null, hasChanged: true, removed: true };
  }, [avatarDataRef]);

  const menuItems = [
    { key: 'upload', label: translateX('upload_image'), icon: <UploadOutlined /> },
    { key: 'camera', label: translateX('take_photo'), icon: <CameraOutlined /> },
    ...(profilePicture
      ? [
          { type: 'divider' },
          { key: 'remove', label: translateX('remove_avatar'), icon: <DeleteOutlined />, danger: true },
        ]
      : []),
  ];

  const onMenuClick = useCallback(({ key }) => {
    if (key === 'upload') {
      uploadRef.current?.querySelector('input[type="file"]')?.click();
    } else if (key === 'camera') {
      setWebcamModalOpen(true);
    } else if (key === 'remove') {
      handleRemove();
    }
    setDropdownOpen(false);
  }, [handleRemove]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Editable avatar */}
      <Flex justify="center" style={{ marginBottom: 20 }}>
        <div style={{ position: 'relative' }}>
          <SimpleAvatar
            src={profilePicture || undefined}
            name={editingRecord?.displayName || 'WA'}
            size={80}
            shape="circle"
            showBorder
            showShadow
            loading={uploading}
          />
          <div
            style={{
              position: 'absolute',
              bottom: -4,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(0, 0, 0, 0.5)',
              borderRadius: 16,
              padding: '3px 10px',
            }}
          >
            <Dropdown
              menu={{ items: menuItems, onClick: onMenuClick }}
              trigger={['click']}
              placement="bottom"
              open={dropdownOpen}
              onOpenChange={setDropdownOpen}
            >
              <CameraOutlined style={{ fontSize: 16, color: '#fff', cursor: 'pointer' }} />
            </Dropdown>
          </div>
        </div>
      </Flex>

      {/* Read-only summary */}
      <Descriptions
        column={1}
        bordered
        size="small"
        colon={false}
        styles={{ label: { width: 100, fontWeight: 500 } }}
        style={{ marginBottom: 20 }}
      >
        <Descriptions.Item label={translateX('phone')}>{formattedPhone || '—'}</Descriptions.Item>
        <Descriptions.Item label={translateX('origin')}>
          <WhatsAppSourceTag sourceType="meta" size="small" />
        </Descriptions.Item>
      </Descriptions>

      {/* Editable fields */}
      <SimpleInput
        name="displayName"
        label={translateX('display_name')}
        required
        maxLength={100}
        autoFocus
      />

      <SimplePassword
        name="accessToken"
        label={`Access Token (Meta) — ${translateX('optional')}`}
        placeholder="(não alterado)"
        maxLength={300}
      />

      {/* Hidden upload trigger */}
      <div ref={uploadRef} style={{ height: 0, overflow: 'hidden' }}>
        <Upload accept="image/*" showUploadList={false} beforeUpload={beforeUpload} disabled={uploading}>
          <button type="button" style={{ display: 'none' }} />
        </Upload>
      </div>

      <AvatarCropModal
        open={cropModalOpen}
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
        imageFile={selectedImageFile}
        onRetakePhoto={imageFromWebcam ? handleRetakePhoto : undefined}
      />

      <WebcamCaptureModal
        open={webcamModalOpen}
        onCancel={() => setWebcamModalOpen(false)}
        onCapture={handleWebcamCapture}
      />
    </>
  );
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const EditMetaPhoneModal = ({ show, setShow, afterClose, editingRecord, onSuccess }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // Bridges avatar state from content component to onFinish handler
  const avatarDataRef = useRef({ file: null, hasChanged: false, removed: false });

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form, goFocus, setLoading }) => {
    const values = form.getFieldsValue(true);
    const { displayName, accessToken } = values;

    setLoading(true);

    try {
      const payload = { displayName };
      if (accessToken) payload.accessToken = accessToken;

      await omnichannelService.updateWhatsappPhone(editingRecord._id, payload);

      // Upload profile picture if user changed it
      const { file, hasChanged, removed } = avatarDataRef.current;
      if (hasChanged) {
        if (removed) {
          await omnichannelService.updateWhatsappProfile(editingRecord._id, { profilePictureBase64: null });
        } else if (file) {
          const base64 = await fileToBase64(file);
          await omnichannelService.updateWhatsappProfile(editingRecord._id, { profilePictureBase64: base64 });
        }
      }

      onSuccess?.();
    } catch (error) {
      if (error?.response?.status === 422) {
        appService.notification({
          flag: 'w',
          title: translateX('invalid_access_token'),
          description: translateX('invalid_access_token_description'),
        });
        goFocus('accessToken');
        return false;
      }
      appService.message({ flag: 'e', text: 'error_saving' });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAfterClose = () => {
    avatarDataRef.current = { file: null, hasChanged: false, removed: false };
    afterClose?.();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => ({
    displayName: editingRecord?.displayName || '',
    accessToken: '',
  }), [editingRecord]);

  const steps = useMemo(() => [{
    stepTitle: 'edit_phone',
    content: () => (
      <EditMetaPhoneContent
        editingRecord={editingRecord}
        avatarDataRef={avatarDataRef}
      />
    ),
    validationFields: ['displayName'],
  }], [editingRecord]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<WhatsAppOutlined style={{ color: '#25D366' }} />}
      title="edit_phone"
      steps={steps}
      onFinish={handleFinish}
      initialValues={initialValues}
      afterClose={handleAfterClose}
      width={460}
      maskClosable={false}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default EditMetaPhoneModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************

function formatPhone(phone) {
  if (!phone) return null;
  const digits = String(phone).trim().replace(/^\+/, '');
  // If already has Brazil country code (55 + DDD = min 12 digits), parse as international
  if (digits.startsWith('55') && digits.length >= 12) {
    return parsePhoneToDisplay(`+${digits}`, undefined, { showCountryCode: true }) || `+${digits}`;
  }
  // Otherwise treat as Brazilian local number (DDD + number, missing +55)
  return parsePhoneToDisplay(digits, 'BR', { showCountryCode: true }) || `+${digits}`;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function validateFileResolution(file) {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.width < MIN_RESOLUTION || img.height < MIN_RESOLUTION) {
        appService.notification({
          flag: 'e',
          title: translateX('invalid_resolution'),
          description: `A imagem precisa ter no mínimo ${MIN_RESOLUTION}x${MIN_RESOLUTION} pixels. A imagem selecionada tem ${img.width}x${img.height} pixels.`,
        });
        resolve(false);
      } else {
        resolve(true);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(true);
    };
    img.src = url;
  });
}
